// Registration service to handle user, institution and payment signup in one call
import urls from '../config/urls';

class RegistrationService {
  
  /**
   * Register a new user with complete data (user + institution + payment) to signup API
   * @param {Object} userData - User registration data
   * @param {Object} institutionData - Institution data (optional)
   * @param {Object} paymentData - Payment data (optional)
   * @returns {Promise} API response
   */
  async registerUser(userData, institutionData = null, paymentData = null) {
    try {
      
      // Prepare the main user data according to API format
      const formData = new URLSearchParams();
      
      // Required user fields (same format as your curl example)
      formData.append('vc_curp', userData.curp || '');
      formData.append('vc_nombre', userData.nombre || '');
      formData.append('vc_apellidoPaterno', userData.apellidoPaterno || '');
      formData.append('vc_apellidoMaterno', userData.apellidoMaterno || '');
      formData.append('id_sexo', userData.sexo || '1'); // 1=M, 2=F
      formData.append('dt_nacimiento', userData.fechaNacimiento || '');
      formData.append('id_entidad', userData.entidad || '15');
      formData.append('id_rol', userData.rol || '857');
      formData.append('vc_telefono', userData.telefono || '');
      formData.append('vc_email', userData.email || '');
      formData.append('vc_password', userData.password || '');
      
      // Add institution and payment data as complement_data (JSON)
      if (institutionData || paymentData) {
        const complementData = {};
        
        // Institution data
        if (institutionData) {
          complementData.institution = {
            nombre: institutionData.nombre,
            direccion: institutionData.direccion,
            telefono: institutionData.telefono,
            email: institutionData.email,
            tipo: institutionData.tipo,
            rfc: institutionData.rfc,
            codigoPostal: institutionData.codigoPostal,
            ciudad: institutionData.ciudad,
            estado: institutionData.estado,
            pais: institutionData.pais || 'México'
          };
        }
        
        // Payment data (all payment info for backend processing)
        if (paymentData) {
          complementData.payment = {
            planId: paymentData.planId,
            planName: paymentData.planName,
            amount: paymentData.amount,
            currency: paymentData.currency || 'MXN',
            metodoPago: paymentData.metodoPago,
            // Card information (backend will process payment)
            tarjeta: paymentData.tarjeta ? {
              numero: paymentData.tarjeta.numero,
              titular: paymentData.tarjeta.titular,
              expiracion: paymentData.tarjeta.expiracion,
              cvv: paymentData.tarjeta.cvv,
              tipo: this.getCardType(paymentData.tarjeta.numero)
            } : null,
            // Billing information
            facturacion: paymentData.facturacion ? {
              requerida: paymentData.facturacion.requerida,
              razonSocial: paymentData.facturacion.razonSocial,
              rfc: paymentData.facturacion.rfc,
              direccion: paymentData.facturacion.direccion,
              codigoPostal: paymentData.facturacion.codigoPostal,
              ciudad: paymentData.facturacion.ciudad,
              estado: paymentData.facturacion.estado
            } : null,
            // Process metadata
            source: 'landing_page',
            timestamp: new Date().toISOString()
          };
        }
        
        // Send all complement data as JSON string
        formData.append('complement_data', JSON.stringify(complementData));
      }
      
      // Single API call to signup with all data
      const response = await fetch(urls.apiEndpoints.signup, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'Accept': 'application/json',
          'Referer': window.location.origin
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        let errorMessage = errorData;
        try {
          const parsed = JSON.parse(errorData);
          if (parsed && parsed.message) {
            errorMessage = parsed.message;
          }
        } catch {
          // No es JSON, dejar el texto original
        }
        console.error('Error HTTP:', response.status, errorMessage);
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      return result;
      
    } catch (error) {
      console.error('❌ ERROR COMPLETO EN REGISTRO:', error);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  }
  
  /**
   * Get available states/entities from API
   * @returns {Promise<Array>}
   */
  async getEntidadesFromAPI() {
    try {
      console.log('🌎 Obteniendo entidades desde API...');
      
      // Use configured entities endpoint
      const endpoint = urls.apiEndpoints.entities ;
      
      // Try with GET first
      let response;
      try {
        response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        });
      } catch (getError) {
        console.log('GET failed, trying POST...');
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({})
        });
      }
      
      if (!response.ok) {
        console.warn('⚠️ API no disponible, usando datos locales');
        return this.getEntidades();
      }
      
      const result = await response.json();
      console.log('📋 Respuesta de API entidades:', result);
      
      // Check if we have entities data
      let entidadesData = [];
      if (result.data?.rows && Array.isArray(result.data.rows)) {
        entidadesData = result.data.rows;
      } else if (result.data && Array.isArray(result.data)) {
        entidadesData = result.data;
      } else if (Array.isArray(result)) {
        entidadesData = result;
      } else {
        console.warn('⚠️ No se encontraron entidades válidas, usando datos locales');
        return this.getEntidades();
      }
      
      console.log('✅ Entidades obtenidas de API:', entidadesData.length);
      
      // Map to expected format
      const mappedEntidades = entidadesData
        .map(entidad => ({
          id: entidad.id,
          name: entidad.vc_nombre || entidad.nombre,
          code: entidad.cve_entidad || entidad.codigo,
          abbreviation: entidad.vc_nombreAbreviatura || entidad.abreviatura,
          active: entidad.sn_activo === 1
        }))
        .filter(entidad => entidad.name && entidad.active !== false);
      
      // If no valid entities, fallback to local data
      return mappedEntidades.length > 0 ? mappedEntidades : this.getEntidades();
      
    } catch (error) {
      console.error('❌ Error obteniendo entidades de API:', error);
      return this.getEntidades(); // Fallback a datos locales
    }
  }
  
  /**
   * Get available municipalities from API based on entity ID
   * @param {string} entidadId - Entity ID
   * @returns {Promise<Array>}
   */
  async getMunicipiosFromAPI(entidadId) {
    try {
      console.log('🏘️ Obteniendo municipios para entidad:', entidadId);
      
      if (!entidadId) {
        console.warn('⚠️ No se proporcionó ID de entidad');
        return [];
      }
      
      // Use configured municipality endpoint
      const endpoint = urls.apiEndpoints.municipalities || 'http://localhost:8000/pacientes/cargarMunicipios';
      
      console.log(`🌐 Consultando endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'Accept': '*/*',
          'Origin': window.location.origin,
          'Referer': window.location.origin + '/'
        },
        body: `text=&cve_entidad=${entidadId}`
      });
      
      if (!response.ok) {
        console.warn('⚠️ Endpoint de municipios no disponible');
        return [];
      }
      
      const result = await response.json();
      console.log('📋 Respuesta de API municipios:', result);
      
      // Handle different response formats
      let municipiosData = [];
      if (Array.isArray(result)) {
        municipiosData = result;
      } else if (result.data && Array.isArray(result.data)) {
        municipiosData = result.data;
      } else if (result.municipios && Array.isArray(result.municipios)) {
        municipiosData = result.municipios;
      } else {
        console.warn('⚠️ Formato de respuesta de municipios no reconocido');
        return [];
      }
      
      console.log('✅ Municipios obtenidos:', municipiosData.length);
      
      // Map to expected format
      const mappedMunicipios = municipiosData
        .map(municipio => ({
          id: municipio.id,
          name: municipio.vc_nombre || municipio.nombre,
          code: municipio.cve_municipio || municipio.codigo,
          entidadId: municipio.cve_entidad || municipio.id_entidad,
          active: municipio.sn_activo === 1
        }))
        .filter(municipio => municipio.name && municipio.active !== false);
      
      return mappedMunicipios;
      
    } catch (error) {
      console.error('❌ Error obteniendo municipios:', error);
      return [];
    }
  }

  /**
   * Validate user data before sending to API
   * @param {Object} userData - User data to validate
   * @returns {Object} Validation result
   */
  validateUserData(userData) {
    const errors = [];
    
    // Required field validations
    if (!userData.email || !this.isValidEmail(userData.email)) {
      errors.push('Email válido es requerido');
    }
    
    if (!userData.password || userData.password.length < 8) {
      errors.push('Password debe tener al menos 8 caracteres');
    }
    
    if (!userData.nombre || userData.nombre.trim().length < 2) {
      errors.push('Nombre es requerido');
    }
    
    if (!userData.apellidoPaterno || userData.apellidoPaterno.trim().length < 2) {
      errors.push('Apellido paterno es requerido');
    }
    
    if (!userData.telefono) {
      errors.push('Teléfono es requerido');
    }
    
    if (!userData.fechaNacimiento) {
      errors.push('Fecha de nacimiento es requerida');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Validate email format
   * @param {string} email 
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  /**
   * Format phone number
   * @param {string} phone 
   * @returns {string}
   */
  formatPhone(phone) {
    // Remove non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XX-XX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8)}`;
    }
    
    return phone;
  }
  
  /**
   * Get card type from number
   * @param {string} cardNumber 
   * @returns {string}
   */
  getCardType(cardNumber) {
    const number = cardNumber.replace(/\D/g, '');
    
    if (/^4/.test(number)) return 'visa';
    if (/^5[1-5]/.test(number)) return 'mastercard';
    if (/^3[47]/.test(number)) return 'amex';
    if (/^6/.test(number)) return 'discover';
    
    return 'unknown';
  }

  /**
   * Get available roles/types
   * @returns {Array}
   */
  getRoles() {
    return [
      { id: 857, name: 'Estudiante', description: 'Estudiante de medicina' },
      { id: 858, name: 'Doctor', description: 'Médico profesional' },
      { id: 859, name: 'Doctor Pro AI', description: 'Médico con acceso a IA' },
      { id: 860, name: 'Institución', description: 'Institución médica' }
    ];
  }
  
  /**
   * Get available states/entities (fallback local data)
   * @returns {Array}
   */
  getEntidades() {
    return [
      { id: '1', name: 'AGUASCALIENTES', code: '01', abbreviation: 'Agc' },
      { id: '2', name: 'BAJA CALIFORNIA', code: '02', abbreviation: 'BC' },
      { id: '3', name: 'BAJA CALIFORNIA SUR', code: '03', abbreviation: 'BCS' },
      { id: '4', name: 'CAMPECHE', code: '04', abbreviation: 'Camp' },
      { id: '5', name: 'COAHUILA', code: '05', abbreviation: 'Coah' },
      { id: '6', name: 'COLIMA', code: '06', abbreviation: 'Col' },
      { id: '7', name: 'CHIAPAS', code: '07', abbreviation: 'Chis' },
      { id: '8', name: 'CHIHUAHUA', code: '08', abbreviation: 'Chih' },
      { id: '9', name: 'CIUDAD DE MÉXICO', code: '09', abbreviation: 'CDMX' },
      { id: '10', name: 'DURANGO', code: '10', abbreviation: 'DGO' },
      { id: '11', name: 'GUANAJUATO', code: '11', abbreviation: 'Gto' },
      { id: '12', name: 'GUERRERO', code: '12', abbreviation: 'Gro' },
      { id: '13', name: 'HIDALGO', code: '13', abbreviation: 'Hgo' },
      { id: '14', name: 'JALISCO', code: '14', abbreviation: 'Jal' },
      { id: '15', name: 'MÉXICO', code: '15', abbreviation: 'Méx' },
      { id: '16', name: 'MICHOACÁN', code: '16', abbreviation: 'Mich' },
      { id: '17', name: 'MORELOS', code: '17', abbreviation: 'Mor' },
      { id: '18', name: 'NAYARIT', code: '18', abbreviation: 'Nay' },
      { id: '19', name: 'NUEVO LEÓN', code: '19', abbreviation: 'NL' },
      { id: '20', name: 'OAXACA', code: '20', abbreviation: 'Oax' },
      { id: '21', name: 'PUEBLA', code: '21', abbreviation: 'Pue' },
      { id: '22', name: 'QUERÉTARO', code: '22', abbreviation: 'Qro' },
      { id: '23', name: 'QUINTANA ROO', code: '23', abbreviation: 'QRoo' },
      { id: '24', name: 'SAN LUIS POTOSÍ', code: '24', abbreviation: 'SLP' },
      { id: '25', name: 'SINALOA', code: '25', abbreviation: 'Sin' },
      { id: '26', name: 'SONORA', code: '26', abbreviation: 'Son' },
      { id: '27', name: 'TABASCO', code: '27', abbreviation: 'Tab' },
      { id: '28', name: 'TAMAULIPAS', code: '28', abbreviation: 'Tams' },
      { id: '29', name: 'TLAXCALA', code: '29', abbreviation: 'Tlax' },
      { id: '30', name: 'VERACRUZ', code: '30', abbreviation: 'Ver' },
      { id: '31', name: 'YUCATÁN', code: '31', abbreviation: 'Yuc' },
      { id: '32', name: 'ZACATECAS', code: '32', abbreviation: 'Zac' }
    ];
  }
}

const registrationServiceInstance = new RegistrationService();
export default registrationServiceInstance;
